<template>
  <div class="my-order-info">
    <div class="my-order-info__container">
      <div v-if="null !== payment" class="my-order-info__payment mobile">
        <span>{{ _T("@Order does not paid") }}</span>
        <OrderPayment :payment="payment" />
      </div>
      <h4 class="my-order-info__title mobile">{{ _T("@Order list") }}</h4>
      <div
        v-for="orderProduct in getProducts(order.products)"
        :key="orderProduct.product.id"
        class="my-order-info__product"
      >
        <div class="my-order-info__content">
          <div class="my-order-info__part-left">
            <ProductLink
              v-if="!orderProduct.product.isDeleted"
              :product="orderProduct.product"
              class="my-order-info__picture"
            >
              <img
                :src="orderProduct.product.getMainImage('medium')"
                :alt="
                  cleanStringsConcat([orderProduct.product.getName(), 'image'])
                "
              />
            </ProductLink>
            <div v-else class="my-order-info__picture">
              <img
                :src="orderProduct.product.getMainImage('medium')"
                :alt="
                  cleanStringsConcat([orderProduct.product.getName(), 'image'])
                "
              />
            </div>
            <div class="my-order-info__subtitle-box">
              <ProductLink
                v-if="!orderProduct.product.isDeleted"
                :product="orderProduct.product"
                class="my-order-info__subtitle"
              >
                {{ orderProduct.product.getName() }}
              </ProductLink>
              <div v-else class="my-order-info__subtitle">
                {{ orderProduct.product.getName() }}
              </div>
              <span class="my-order-info__code">
                {{ _T("@Code") }} {{ orderProduct.product.code }}
              </span>
              <div class="my-order-info__count mobile">
                <span>{{ _T("@Amount") }}</span>
                <span>{{ orderProduct.quantity }}</span>
              </div>
            </div>
          </div>
          <div class="my-order-info__part-right">
            <div class="my-order-info__box">
              <span class="my-order-info__count desktop">
                {{ orderProduct.quantity }} шт
              </span>
              <div class="my-order-info__price">
                <span class="my-order-info__price text">
                  {{ _T("@Price") }}
                </span>
                <span class="my-order-info__price number">
                  {{ priceFormatter(orderProduct.totalSalePrice.amount) }}
                  &#8372;
                </span>
              </div>
            </div>
          </div>
        </div>

        <template v-if="0 !== orderProduct.gifts.length">
          <ProductGift
            v-for="giftProduct in orderProduct.gifts"
            :key="giftProduct.id"
            :product="new Product(giftProduct)"
            :quantity="orderProduct.quantity"
          />
        </template>
      </div>
    </div>
    <div class="my-order-info__footer">
      <div class="my-order-info__delivery">
        <h4 class="my-order-info__title">{{ _T("@Delivery") }}</h4>
        <table>
          <tr
            v-if="
              order.parcels.length &&
              null !== order.parcels[0].scheduledDeliveryAt
            "
          >
            <td>{{ _T("@Scheduled arrival time") }}</td>
            <td>
              {{ normalizeDate(order.parcels[0].scheduledDeliveryAt) }}
            </td>
          </tr>
          <tr>
            <td>{{ _T("@Delivery type") }}</td>
            <td>
              {{
                deliveryMethod[order.delivery.type.method](
                  order.delivery.address,
                )
              }}
            </td>
          </tr>
          <tr>
            <td>{{ _T("@Delivery address") }}</td>
            <td>
              {{ formatAddress(order.delivery.address) }}
            </td>
          </tr>
          <tr>
            <td>{{ _T("@Recipient") }}:</td>
            <td>
              {{ getFullName(order.recipient.name) }}
            </td>
          </tr>
          <tr>
            <td>{{ _T("@Phone") }}:</td>
            <td>
              {{ formatPhoneInternational(order.recipient.phone) }}
            </td>
          </tr>
          <tr v-if="order.parcels.length">
            <td>ТТН:</td>
            <td>
              {{ order.parcels[0].number }}
            </td>
          </tr>
        </table>
      </div>
      <div class="my-order-info__pay">
        <h4 class="my-order-info__title">{{ _T("@Payment") }}</h4>
        <table>
          <tr>
            <td>{{ _T("@Payment") }}:</td>
            <td>
              {{ getPaymentMethod(order.payment) }}
            </td>
          </tr>
          <template
            v-if="
              calcDiscount(
                order.totalPrice.amount,
                order.totalSalePrice.amount,
              ) > 0
            "
          >
            <tr>
              <td>{{ _T("@Order price") }}:</td>
              <td>{{ priceFormatter(order.totalSalePrice.amount) }} &#8372;</td>
            </tr>
            <tr class="my-order-info__discount">
              <td>{{ _T("@Discount") }}:</td>
              <td>
                {{
                  priceFormatter(
                    calcDiscount(
                      order.totalPrice.amount,
                      order.totalSalePrice.amount,
                    ),
                  )
                }}
                &#8372;
              </td>
            </tr>
          </template>
          <tr>
            <td>{{ _T("@Delivery") }}:</td>
            <MyLink
              name="delivery"
              class="my-order-info__delivery-info"
              :class="{ free: isFreeDelivery }"
            >
              {{ isFreeDelivery ? _T("@Free") : _T("@Paid") }}
              <span v-if="isFreeDelivery">&#x2a;</span>
            </MyLink>
          </tr>
          <tr class="my-order-info__total">
            <td>{{ _T("@Total") }}:</td>
            <td>{{ priceFormatter(order.totalPrice.amount) }} &#8372;</td>
          </tr>
        </table>
        <div v-if="null !== payment" class="my-order-info__payment">
          <span>{{ _T("@Order does not paid") }}</span>
          <OrderPayment :payment="payment" />
        </div>
        <div v-if="isOrderPaid(order)" class="my-order-info__paid">
          {{ _T("@Order has been paid") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Product } from "~/models/product.model";
import { isFreeDeliveryOrder } from "~/utils/deliveryFormatter";
import ProductGift from "~/modules/shared/productGift/ProductGift.vue";
import {
  getPayment,
  getPaymentMethod,
  isOrderPaid,
} from "~/utils/paymentFormatter.js";
import { formatPhoneInternational } from "~/utils/phoneHelper.js";
import OrderPayment from "~/modules/order/components/OrderPayment.vue";

const props = defineProps({
  order: { type: Object, required: true },
});

const payment = ref(null);

function calcDiscount(total, saleTotal) {
  return total - saleTotal;
}

const getProducts = (products) => {
  return products.map((productDto) => {
    return {
      ...productDto,
      product: new Product(productDto.product),
    };
  });
};

const isFreeDelivery = computed(() => {
  return isFreeDeliveryOrder(props.order);
});

onMounted(async () => {
  payment.value = await getPayment(props.order);
});
</script>

<style lang="scss" scoped>
.my-order-info {
  &__container {
    @include flex-container(column);
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid var(--color-sky-base);
  }

  &__title {
    color: var(--color-ink-light);
    @include font(16, 22, 700);

    &.mobile {
      @include mobile {
        display: none;
      }
    }
  }

  &__product {
    @include flex-container(column, flex-start, flex-end);
    gap: 8px;

    padding-bottom: 8px;

    &:not(:last-of-type) {
      border-bottom: 1px solid var(--color-sky-base);
    }
  }

  &__content {
    width: 100%;

    @include flex-container(row, space-between, center);
    color: var(--color-ink-light);

    @include smallestScreen {
      @include flex-container(column, center);
    }
  }

  &__part-left {
    @include flex-container(row, flex-start, center);
    gap: 32px;

    @include smallestScreen {
      width: 100%;
    }

    @include mobile {
      gap: 8px;
      margin-bottom: 8px;
    }
  }

  &__part-right {
    @include flex-container(row, flex-end, center);
    min-width: 50%;

    @include smallestScreen {
      min-width: auto;
      width: 100%;

      justify-content: center;

      margin-bottom: 16px;
    }

    @include mobile {
      justify-content: space-between;
    }
  }

  &__picture {
    @include fixedHW(120px);
    display: flex;

    @include bigMobile {
      @include fixedHW(96px);
    }

    @include mobile {
      @include fixedHW(72px);
    }
  }

  &__subtitle-box {
    @include flex-container(column, center, flex-start);
    gap: 8px;
  }

  &__subtitle {
    max-width: 500px;
    width: 100%;

    @include font(16, 20, 400);

    transition: color 0.2s;

    @include smallestScreen {
      max-width: 100%;
    }

    &:hover {
      color: var(--color-primary-base);
    }
  }

  &__code {
    @include font(12, 16);
  }

  &__code-modal {
    @include flex-container(column, center, center);
    gap: 8px;

    @include mobile {
      display: none;
    }
  }

  &__code-overflow {
    @include flex-container(column, center, center);
    gap: 8px;
  }

  &__code-box {
    @include flex-container(row, flex-end, center);
    gap: 8px;

    border-radius: 8px;
    border: 1px solid var(--color-sky-lighter);

    padding: 4px 16px;
  }

  &__sn-code {
    @include font(16, 18, 400);
    text-align: start;

    min-width: 170px;
    width: 100%;

    @include mobile {
      @include font(12, 18, 400);
    }
  }

  &__code-show {
    display: none;

    &.active {
      cursor: pointer;
      display: inline-block;

      @include font(14, 24, 600);
      color: var(--color-primary-base);

      border-bottom: 1px dashed var(--color-primary-base);
    }
  }

  &__code-mob {
    display: none;

    @include mobile {
      cursor: pointer;
      @include flex-container(row, center, center);
      padding: 8px;
    }
  }

  &__code-mob-text {
    @include font(14, 18, 400);
    color: var(--color-primary-base);

    border-bottom: 1px solid var(--color-primary-base);
    margin-left: 8px;
  }

  &__code-copy {
    cursor: pointer;

    transition: transform 0.2s;

    @include mobile {
      width: 18px;
      height: 18px;
    }

    &:hover {
      transform: scale(1.1);
    }
  }

  &__box {
    max-width: 240px;
    width: 100%;
    @include flex-container(row, space-between, center);
    gap: 20px;

    @include mobile {
      max-width: max-content;
    }
  }

  &__count {
    @include font(16, 20, 400);

    &.mobile {
      display: none;

      @include mobile {
        display: block;
        @include font(12, 16, 400);
      }

      & span:first-of-type {
        @include mobile {
          margin-right: 8px;
        }
      }
    }

    &.desktop {
      margin-left: 16px;

      @include mobile {
        display: none;
      }
    }
  }

  &__price {
    @include flex-container(row, flex-start, center);
    gap: 8px;

    &.text {
      display: none;

      @include mobile {
        display: inline;
        @include font(14, 18, 500);
      }
    }

    &.number {
      @include font(16, 22, 700);
    }
  }

  &__footer {
    padding: 16px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    @include bigMobile {
      grid-template-columns: auto;
    }
  }

  &__delivery {
    margin-right: 70px;

    @include bigMobile {
      margin-right: 0;

      padding-bottom: 16px;
    }
  }

  &__pay {
    @include bigMobile {
      padding-top: 16px;
      border-top: 1px solid var(--color-sky-base);
    }
  }

  & tr {
    &:not(:last-of-type) td {
      padding-bottom: 16px;

      @include mobile {
        padding-bottom: 8px;
      }
    }

    &:not(:last-of-type) {
      @include mobile {
        margin-bottom: 16px;
      }
    }

    @include mobile {
      display: grid;
    }
  }

  & td {
    padding-right: 8px;
    vertical-align: text-top;

    &:first-of-type {
      width: 35%;
      @include font(16, 22, 600);
      color: var(--color-sky-darker);

      @include mobile {
        width: 55%;
      }
    }

    &:nth-of-type(2) {
      width: 50%;

      @include font(16, 22, 500);
      color: var(--color-ink-light);

      @include mobile {
        width: 100%;
      }
    }
  }

  &__pay td {
    width: auto;

    &:first-of-type {
      width: 35%;

      @include mobile {
        width: 55%;
      }
    }

    &:nth-of-type(2) {
      width: 65%;

      @include mobile {
        width: 100%;
      }
    }
  }

  &__discount td {
    &:nth-of-type(2) {
      color: var(--color-green-base-status);
    }
  }

  &__delivery-info {
    @include font(16, 22, 500);
    color: var(--color-grey-900);
    text-decoration: underline;

    &.free {
      color: var(--color-green-base-status);
    }

    & span {
      color: var(--color-red-base);
    }
  }

  &__total td {
    &:first-of-type {
      @include font(24, 34, 600);

      @include mobile {
        @include font(20, 28, 500);
      }
    }

    &:nth-of-type(2) {
      @include font(28, 39, 700);
      color: var(--color-primary-base);

      @include mobile {
        @include font(20, 28, 700);
      }
    }
  }

  &__payment {
    max-width: 300px;

    @include flex-container(column, flex-start, flex-start);
    gap: 8px;

    color: var(--color-red-error-500);

    padding-top: 16px;

    &.mobile {
      display: none;

      @include bigMobile {
        display: flex;
        padding: 0;
      }
    }
  }

  &__paid {
    color: var(--color-green-base-status);
    padding-top: 16px;
  }
}
</style>
